import { MapElement, MapSettings } from '../../../types'
import { Point } from './types'
import { Tool } from '../ToolsPanel'
import { applySnapping } from '../canvas/snapping'
import {
  calculatePolygonArea,
  calculateCurvedPolygonArea,
} from '../canvas/geometryUtils'

// Define layer ordering from bottom to top
const LAYER_ORDER = {
  room: 1,
  polygon: 2,
  wall: 10,
  stairs: 15,
  door: 20,
  window: 21,
  line: 30,
  rectangle: 31,
  circle: 32,
  'fixture-rect': 40,
  'fixture-line': 41,
  'fixture-point': 42,
  'fixture-circle': 43,
  'fixture-polygon': 44,
  text: 100,
  poi: 101,
} as const

const getElementZIndex = (elementType: string): number => {
  return LAYER_ORDER[elementType as keyof typeof LAYER_ORDER] || 50
}

const getCanvasCoordinates = (
  clientX: number,
  clientY: number,
  canvas: HTMLCanvasElement | null,
  panOffset: Point,
  zoom: number,
  elements: MapElement[],
  mapSettings: MapSettings,
): Point => {
  if (!canvas) return { x: 0, y: 0 }

  const rect = canvas.getBoundingClientRect()
  const x = (clientX - rect.left - panOffset.x * zoom) / zoom
  const y = (clientY - rect.top - panOffset.y * zoom) / zoom

  return applySnapping({ x, y }, elements, mapSettings)
}

const getCursorClass = (
  isDragging: boolean,
  activeTool: Tool,
  hoveredElement: MapElement | null,
  hoveredHandle:
    | 'start'
    | 'end'
    | 'vertex'
    | 'label'
    | 'controlPoint1'
    | 'controlPoint2'
    | null,
): string => {
  if (isDragging) {
    return 'pan-cursor' // Use grabbing cursor while dragging
  }

  // Show pointer cursor when hovering over handles
  if (activeTool === 'select' && hoveredElement && hoveredHandle) {
    return 'select-cursor'
  }

  switch (activeTool) {
    case 'select':
      return 'select-cursor'
    case 'pan':
      return 'pan-cursor'
    default:
      return ''
  }
}

const createElementFromDrawing = (
  activeTool: Tool,
  drawStart: Point,
  mousePos: Point,
  mapSettings: MapSettings,
): MapElement | null => {
  if (!drawStart) return null

  const id = Date.now().toString()
  const commonProps = {
    color: '#333',
    strokeWidth: 2,
  }

  // Base element with auto-assigned z-index for intelligent layering
  const baseElement = {
    id,
    floor: mapSettings.currentFloor,
  }

  // Calculate real-world measurements
  const pixelWidth = Math.abs(mousePos.x - drawStart.x)
  const pixelHeight = Math.abs(mousePos.y - drawStart.y)
  const realWidth = pixelWidth / mapSettings.scale
  const realHeight = pixelHeight / mapSettings.scale
  const lineLength =
    Math.sqrt(
      Math.pow(mousePos.x - drawStart.x, 2) +
        Math.pow(mousePos.y - drawStart.y, 2),
    ) / mapSettings.scale

  switch (activeTool) {
    case 'wall':
      return {
        ...baseElement,
        type: 'wall',
        position: drawStart,
        properties: {
          color: '#8B4513',
          thickness: 6,
          endPosition: mousePos,
          material: 'drywall', // Explicitly set default
          wallType: 'interior', // Explicitly set default
          loadBearing: false, // Explicitly set default
          height: mapSettings.units === 'feet' ? 9 : 2.74, // 9 feet or 2.74m standard
          label: 'Wall', // Default label
        },
        measurements: {
          realLength: lineLength,
          realHeight: mapSettings.units === 'feet' ? 9 : 2.74,
        },
        zIndex: getElementZIndex('wall'),
      }

    case 'door':
      return {
        ...baseElement,
        type: 'door',
        position: drawStart,
        properties: {
          color: '#D2691E',
          width: mapSettings.units === 'feet' ? 36 : 91, // 3 feet or 91cm
          height: mapSettings.units === 'feet' ? 84 : 213, // 7 feet or 213cm standard door height
          endPosition: mousePos,
          doorType: 'single', // Explicitly set default
          swingDirection: 'left', // Explicitly set default
          material: 'wood', // Explicitly set default
          label: 'Door', // Default label
        },
        measurements: {
          realWidth: mapSettings.units === 'feet' ? 3 : 0.91,
          realHeight: mapSettings.units === 'feet' ? 7 : 2.13,
          realLength: lineLength,
        },
        zIndex: getElementZIndex('door'),
      }

    case 'window':
      return {
        ...baseElement,
        type: 'window',
        position: drawStart,
        properties: {
          color: '#4169E1',
          endPosition: mousePos,
          width: mapSettings.units === 'feet' ? 36 : 91, // 3 feet or 91cm
          height: mapSettings.units === 'feet' ? 48 : 122, // 4 feet or 122cm standard window height
          windowType: 'double-hung', // Explicitly set default
          glazing: 'double', // Explicitly set default (double pane)
          frameMaterial: 'vinyl', // Explicitly set default
          label: 'Window', // Default label
        },
        measurements: {
          realWidth: mapSettings.units === 'feet' ? 3 : 0.91,
          realHeight: mapSettings.units === 'feet' ? 4 : 1.22,
          realLength: lineLength,
        },
        zIndex: getElementZIndex('window'),
      }

    case 'room':
      const width = mousePos.x - drawStart.x
      const height = mousePos.y - drawStart.y
      return {
        ...baseElement,
        type: 'room',
        position: drawStart,
        properties: {
          color: '#4a90e2',
          fillColor: 'rgba(200, 220, 255, 0.3)',
          width: Math.abs(width),
          height: Math.abs(height),
          roomName: 'Room',
          roomType: 'general', // Explicitly set default
          ceilingHeight: mapSettings.units === 'feet' ? 9 : 2.74, // 9 feet or 2.74m standard
          floorMaterial: 'hardwood', // Explicitly set default
          label: 'Room', // Default label
        },
        measurements: {
          realWidth: realWidth,
          realLength: realHeight,
          realArea: realWidth * realHeight,
        },
        zIndex: getElementZIndex('room'),
      }

    case 'stairs':
      const stairWidth = mousePos.x - drawStart.x
      const stairHeight = mousePos.y - drawStart.y
      return {
        ...baseElement,
        type: 'stairs',
        position: drawStart,
        properties: {
          color: '#696969',
          fillColor: '#ddd',
          width: Math.abs(stairWidth),
          height: Math.abs(stairHeight),
          steps: 12,
          label: 'Stairs',
          direction: 'up',
          material: 'wood',
          stairType: 'straight', // Explicitly set default
          riserHeight: 7, // 7 inches standard
          treadDepth: 11, // 11 inches standard
          hasHandrail: true, // Explicitly set default
        },
        measurements: {
          realWidth: realWidth,
          realLength: realHeight,
        },
        zIndex: getElementZIndex('stairs'),
      }

    case 'line':
      return {
        ...baseElement,
        type: 'line',
        position: drawStart,
        properties: {
          ...commonProps,
          endPosition: mousePos,
        },
        measurements: {
          realLength: lineLength,
        },
        zIndex: getElementZIndex('line'),
      }

    case 'rectangle':
      const rectWidth = mousePos.x - drawStart.x
      const rectHeight = mousePos.y - drawStart.y
      return {
        ...baseElement,
        type: 'rectangle',
        position: drawStart,
        properties: {
          ...commonProps,
          width: Math.abs(rectWidth),
          height: Math.abs(rectHeight),
          fillColor: 'transparent',
        },
        measurements: {
          realWidth: realWidth,
          realLength: realHeight,
        },
        zIndex: getElementZIndex('rectangle'),
      }

    case 'circle':
      const radius = Math.sqrt(
        Math.pow(mousePos.x - drawStart.x, 2) +
          Math.pow(mousePos.y - drawStart.y, 2),
      )
      return {
        ...baseElement,
        type: 'circle',
        position: drawStart,
        properties: {
          ...commonProps,
          radius,
          fillColor: 'transparent',
        },
        measurements: {
          realWidth: (radius * 2) / mapSettings.scale,
        },
        zIndex: getElementZIndex('circle'),
      }

    case 'fixture-rect':
      return {
        ...baseElement,
        type: 'fixture-rect',
        position: {
          x: Math.min(drawStart.x, mousePos.x),
          y: Math.min(drawStart.y, mousePos.y),
        },
        properties: {
          ...commonProps,
          width: Math.abs(mousePos.x - drawStart.x),
          height: Math.abs(mousePos.y - drawStart.y),
          fillColor: 'rgba(255, 224, 178, 0.4)',
          fixtureCategory: 'counter',
          fixtureType: 'kitchen',
          material: 'granite', // Explicitly set default
          label: 'Kitchen Counter', // Explicitly set default
        },
        measurements: {
          realWidth,
          realHeight,
          realArea: realWidth * realHeight,
        },
        zIndex: getElementZIndex('fixture-rect'),
      }

    case 'fixture-line':
      return {
        ...baseElement,
        type: 'fixture-line',
        position: drawStart,
        properties: {
          ...commonProps,
          endPosition: mousePos,
          color: '#4a90e2',
          strokeWidth: 4,
          fixtureCategory: 'wall-mount',
          fixtureType: 'tv',
          material: 'metal', // Explicitly set default
          label: 'Wall Mount', // Explicitly set default
        },
        measurements: {
          realLength: lineLength,
        },
        zIndex: getElementZIndex('fixture-line'),
      }

    case 'fixture-point':
      return {
        ...baseElement,
        type: 'fixture-point',
        position: mousePos,
        properties: {
          ...commonProps,
          size: 20,
          color: '#4a90e2',
          fixtureCategory: 'plumbing',
          fixtureType: 'sink',
          material: 'porcelain', // Explicitly set default
          label: 'Sink', // Explicitly set default
        },
        zIndex: getElementZIndex('fixture-point'),
      }

    case 'fixture-circle':
      const fixtureRadius = Math.sqrt(
        Math.pow(mousePos.x - drawStart.x, 2) +
          Math.pow(mousePos.y - drawStart.y, 2),
      )
      return {
        ...baseElement,
        type: 'fixture-circle',
        position: drawStart,
        properties: {
          ...commonProps,
          color: '#9b59b6',
          radius: fixtureRadius,
          fillColor: 'rgba(155, 89, 182, 0.2)',
          fixtureCategory: 'circular',
          fixtureType: 'table',
          material: 'wood', // Explicitly set default
          label: 'Round Table', // Explicitly set default
        },
        measurements: {
          realWidth: (fixtureRadius * 2) / mapSettings.scale,
          realHeight: (fixtureRadius * 2) / mapSettings.scale,
          realArea: Math.PI * Math.pow(fixtureRadius / mapSettings.scale, 2),
        },
        zIndex: getElementZIndex('fixture-circle'),
      }

    case 'fixture-polygon':
      // This is handled separately in MapCanvas with polygon drawing mode
      return null

    default:
      return null
  }
}

const updatePolygonMeasurements = (
  vertices: Point[],
  mapSettings: MapSettings,
  curvedEdges?: {
    edgeIndex: number
    controlPoint1: Point
    controlPoint2: Point
    curvature: number
  }[],
) => {
  // Recalculate area and dimensions accounting for curved edges
  const pixelArea =
    curvedEdges && curvedEdges.length > 0
      ? calculateCurvedPolygonArea(vertices, curvedEdges)
      : calculatePolygonArea(vertices)
  const realArea = pixelArea / (mapSettings.scale * mapSettings.scale)

  // Calculate bounding box for approximate dimensions
  let minX = vertices[0].x,
    maxX = vertices[0].x
  let minY = vertices[0].y,
    maxY = vertices[0].y

  vertices.forEach(vertex => {
    minX = Math.min(minX, vertex.x)
    maxX = Math.max(maxX, vertex.x)
    minY = Math.min(minY, vertex.y)
    maxY = Math.max(maxY, vertex.y)
  })

  const pixelWidth = maxX - minX
  const pixelHeight = maxY - minY
  const realWidth = pixelWidth / mapSettings.scale
  const realHeight = pixelHeight / mapSettings.scale

  return {
    realWidth,
    realLength: realHeight,
    realArea,
  }
}

const getLabelPosition = (element: MapElement): Point | null => {
  // Check if element has a label to display
  const hasLabel =
    element.properties.roomName ||
    element.properties.label ||
    ((element.type === 'fixture-rect' ||
      element.type === 'fixture-circle' ||
      element.type === 'fixture-polygon') &&
      element.properties.fixtureType)

  if (!hasLabel) {
    return null
  }

  let defaultX = 0,
    defaultY = 0

  switch (element.type) {
    case 'room':
    case 'rectangle':
    case 'fixture-rect':
      if (element.properties.width && element.properties.height) {
        defaultX = element.position.x + element.properties.width / 2
        defaultY = element.position.y + element.properties.height / 2
      }
      break
    case 'circle':
    case 'fixture-circle':
      if (element.properties.radius) {
        defaultX = element.position.x + element.properties.radius
        defaultY = element.position.y + element.properties.radius
      }
      break
    case 'polygon':
    case 'fixture-polygon':
      if (element.properties.vertices) {
        const vertices = element.properties.vertices as Point[]
        vertices.forEach(vertex => {
          defaultX += vertex.x
          defaultY += vertex.y
        })
        defaultX /= vertices.length
        defaultY /= vertices.length
      }
      break
    default:
      return null
  }

  const labelOffsetX = element.labelPosition?.x || 0
  const labelOffsetY = element.labelPosition?.y || 0

  return {
    x: defaultX + labelOffsetX,
    y: defaultY + labelOffsetY,
  }
}

const isPointInLabel = (point: Point, element: MapElement): boolean => {
  const labelPos = getLabelPosition(element)
  if (!labelPos) return false

  // For fixture elements, also consider fixtureType as a fallback label
  let labelText = element.properties.roomName || element.properties.label || ''
  if (
    (element.type === 'fixture-rect' ||
      element.type === 'fixture-circle' ||
      element.type === 'fixture-polygon') &&
    !labelText
  ) {
    labelText = element.properties.fixtureType || ''
  }

  if (!labelText) return false

  // Larger font size for fixture labels, regular for others
  const isFixture = element.type.startsWith('fixture-')
  const fontSize = isFixture ? 16 : 14
  const textWidth = labelText.length * fontSize * 0.7 // Approximate text width (slightly wider)
  const textHeight = fontSize * 1.5 // Taller hit area for easier targeting

  // Check if point is within label bounds (approximate) - larger hit area for fixtures
  const hitPadding = isFixture ? 10 : 5 // Extra padding for fixture labels
  return (
    point.x >= labelPos.x - textWidth / 2 - hitPadding &&
    point.x <= labelPos.x + textWidth / 2 + hitPadding &&
    point.y >= labelPos.y - textHeight / 2 - hitPadding &&
    point.y <= labelPos.y + textHeight / 2 + hitPadding
  )
}

const getElementLayer = (element: MapElement) => {
  if (
    element.type === 'fixture-rect' ||
    element.type === 'fixture-line' ||
    element.type === 'fixture-point' ||
    element.type === 'fixture-circle' ||
    element.type === 'fixture-polygon'
  ) {
    return 'fixtures'
  }
  if (element.type === 'text' || element.type === 'poi') {
    return 'annotations'
  }
  if (element.type === 'measurement') {
    return 'measurements'
  }
  if (
    element.type === 'wall' ||
    element.type === 'door' ||
    element.type === 'window' ||
    element.type === 'room' ||
    element.type === 'stairs' ||
    element.type === 'polygon'
  ) {
    return 'architecture'
  }
  return 'architecture' // default
}

export {
  LAYER_ORDER,
  getElementZIndex,
  getCanvasCoordinates,
  getCursorClass,
  createElementFromDrawing,
  updatePolygonMeasurements,
  getLabelPosition,
  isPointInLabel,
  getElementLayer,
}
